import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryCoachSurveyParams } from '../../../services/api/dataLibrary';
import type {
  DataLibraryCoachSurveyRow,
  DataLibrarySurveyQuestion,
  DataLibraryCoachSurveyFilterOptions,
} from '../../../types';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibraryCoachSurveyResponsesProps {
  selectedCohortIds: string[];
  surveyType: 'midpoint' | 'endpoint';
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

// Static columns for coach survey tabs — no attendance, group, or flag columns
const STATIC_COLUMNS: ColumnConfig[] = [
  { id: 'cohort_name', label: 'Cohort', visible: true, sortable: false },
  { id: 'cohort_health', label: 'Cohort Health', visible: false, sortable: false, groupable: false, exportKey: null },
  { id: 'full_name', label: 'Coach Name', visible: true, sortable: false },
  { id: 'email', label: 'Email', visible: true, sortable: true, groupable: false },
  { id: 'submitted_at', label: 'Submitted', visible: true, sortable: true },
  { id: 'overall_satisfaction', label: 'Overall Satisfaction', visible: true, sortable: false },
];

export function useDataLibraryCoachSurveyResponses({
  selectedCohortIds,
  surveyType,
  activeGroup,
}: UseDataLibraryCoachSurveyResponsesProps) {
  // Data state
  const [rows, setRows] = useState<DataLibraryCoachSurveyRow[]>([]);
  const [questions, setQuestions] = useState<DataLibrarySurveyQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryCoachSurveyFilterOptions>({
    cohorts: [],
  });
  const [hasSurveyConfig, setHasSurveyConfig] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'first_name',
    order: 'ASC',
  });

  // Filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);

  // Column config
  const defaultColumns: ColumnConfig[] = useMemo(() => {
    const GROUPABLE_QUESTION_TYPES = new Set(['choices', 'yesNo', 'scale']);
    const questionCols: ColumnConfig[] = questions.map(q => ({
      id: `q_${q.question_id}`,
      label: q.question_text,
      visible: true,
      sortable: false,
      groupable: GROUPABLE_QUESTION_TYPES.has(q.question_type),
    }));

    return [...STATIC_COLUMNS, ...questionCols];
  }, [questions]);

  // MDF-209: saved order and visibility for this tab
  const { columns, setColumns } = useDataLibraryColumns(defaultColumns);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params
  const buildParams = useCallback((): DataLibraryCoachSurveyParams => {
    const params: DataLibraryCoachSurveyParams = {
      cohortIds: selectedCohortIds,
      surveyType,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    return params;
  }, [selectedCohortIds, surveyType, page, pageSize, sortConfig, debouncedSearch]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setRows([]);
      setQuestions([]);
      setTotalCount(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getCoachSurveyResponses({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getCoachSurveyResponses(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setRows(result.data || []);
      setQuestions(result.meta.questions || []);
      setTotalCount(result.meta.pagination.total);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
      setHasSurveyConfig(result.meta.hasSurveyConfig);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load coach survey responses',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Client-side filters (overall_satisfaction, cohort)
  const filteredRows = useMemo(() => {
    let result = rows;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'overall_satisfaction') {
        result = result.filter(r => r.overall_satisfaction && filter.values.includes(r.overall_satisfaction));
      }
      if (filter.key === 'cohort') {
        result = result.filter(r => filter.values.includes(r.cohort_id));
      }
    }

    return result;
  }, [rows, activeFilters]);

  // Client-side grouping
  const groupedRows = useMemo(() => {
    return groupDataByLevels(
      filteredRows as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibraryCoachSurveyRow>[] | null;
  }, [filteredRows, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows: filteredRows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
  };
}
